import dayjs from 'dayjs';
import React from 'react';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { HomeLinkRoutes } from 'constants/routes';
import { ScreenView, ViewMode } from 'views/Medical/types';

export interface DialogViewState {
  dialogView:
    | 'viewView'
    | 'confirmView'
    | 'viewCancelView'
    | 'viewHourSettingDialog'
    | null;
  selectedAppointment?: string;
}

const initDialogViewState: DialogViewState = {
  dialogView: null,
};

export const getMedicalScreenView = (pathname: string): ScreenView => {
  if (pathname.includes(HomeLinkRoutes.medicalAppointments)) {
    return ScreenView.calendarView;
  }
  if (pathname.includes(HomeLinkRoutes.medicalSchedule)) {
    return ScreenView.hoursView;
  }
  if (pathname.includes(HomeLinkRoutes.medicalPaymentHistory)) {
    return ScreenView.paymentHistoryView;
  }
  return ScreenView.calendarView;
};

const MedicalViewContext = createContext<{
  pathname: string;
  screenView: ScreenView;
  isDoctorRoot: boolean;
  isSelectedAppointments: boolean;
  isSelectedScheduleView: boolean;
  isSelectedPaymentHistoryView: boolean;
  viewMode: ViewMode;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;
  editDayDialogOpen: boolean;
  setEditDayDialogOpen: Dispatch<SetStateAction<boolean>>;
  selectedDate: string;
  setSelectedDate: Dispatch<SetStateAction<string>>;
  dialogViewState: DialogViewState;
  setDialogViewState: Dispatch<SetStateAction<DialogViewState>>;
  isLeftMenuOpen: boolean;
  setIsLeftMenuOpen: Dispatch<SetStateAction<boolean>>;
}>({
  pathname: '',
  screenView: ScreenView.hoursView,
  isDoctorRoot: false,
  isSelectedAppointments: false,
  isSelectedScheduleView: false,
  isSelectedPaymentHistoryView: false,
  viewMode: ViewMode.month,
  setViewMode: () => {},
  editDayDialogOpen: false,
  setEditDayDialogOpen: () => {},
  selectedDate: '',
  setSelectedDate: () => {},
  dialogViewState: initDialogViewState,
  setDialogViewState: () => {},
  isLeftMenuOpen: false,
  setIsLeftMenuOpen: () => {},
});

const MedicalViewProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.month);
  const [editDayDialogOpen, setEditDayDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format('YYYY-MM-DD'),
  );
  const [dialogViewState, setDialogViewState] =
    useState<DialogViewState>(initDialogViewState);

  const screenView = useMemo(() => {
    return getMedicalScreenView(pathname);
  }, [pathname]);

  const isDoctorRoot = useMemo(() => {
    return pathname === HomeLinkRoutes.medical;
  }, [pathname]);

  const isSelectedAppointments = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalAppointments);
  }, [pathname]);

  const isSelectedScheduleView = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalSchedule);
  }, [pathname]);

  const isSelectedPaymentHistoryView = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalPaymentHistory);
  }, [pathname]);

  return (
    <MedicalViewContext.Provider
      value={{
        pathname,
        screenView,
        isDoctorRoot,
        isSelectedAppointments,
        isSelectedScheduleView,
        isSelectedPaymentHistoryView,
        viewMode,
        setViewMode,
        editDayDialogOpen,
        setEditDayDialogOpen,
        selectedDate,
        setSelectedDate,
        dialogViewState,
        setDialogViewState,
        isLeftMenuOpen,
        setIsLeftMenuOpen,
      }}
    >
      {children}
    </MedicalViewContext.Provider>
  );
};

export default MedicalViewProvider;

export const useMedicalView = () => {
  return useContext(MedicalViewContext);
};
